<template>
    <BaseFormWrapper :id="id">
        <!-- Eyebrow -->
        <div class="flex h-auto w-full items-start justify-start space-x-2">
            <ToggleSwitch
                v-model="form.hasEyebrow"
                :label="$t('has_eyebrow')"
                :name="`${id}-hasEyebrow`"
            />
            <ToggleSwitch
                v-if="form.hasEyebrow"
                v-model="form.eyebrow.isBadge"
                :label="$t('is_badge')"
                :name="`${id}-isBadge`"
            />
        </div>
        <template v-if="form.hasEyebrow">
            <TextField
                v-model="form.eyebrow.text[activeLocale]"
                :name="`${id}-eyebrow.text`"
                :label="$t('eyebrow_text')"
                help="Eyebrow Text"
                :required="true"
            />
            <SelectField
                v-model="form.eyebrow.color"
                :name="`${id}-eyebrow.color`"
                :label="$t('eyebrow_color')"
                help="Eyebrow Color"
                :options="colorOptions"
            />
            <TextField
                v-model="form.eyebrow.icon"
                :name="`${id}-eyebrow.icon`"
                :label="$t('eyebrow_icon')"
                :help="$t('icon_class_name_example')"
            />
        </template>

        <!-- Title & Description -->
        <TextField
            v-model="form.title[activeLocale]"
            :name="`${id}-title`"
            :label="$t('title')"
            help="Title"
            :required="true"
        />
        <div class="my-4 w-full">
            <label
                class="mb-1 block text-sm font-medium"
                :for="`${id}-description`"
            >
                {{ $t('description') }}
            </label>
            <textarea
                :id="`${id}-description`"
                v-model="form.description[activeLocale]"
                :name="`${id}-description`"
                class="form-input h-56 w-full"
            />
        </div>
        <SelectField
            v-model="form.gridOrientation"
            :name="`${id}-gridOrientation`"
            :label="$t('grid_orientation')"
            help="Grid layout orientation"
            :options="orientationOptions"
        />
        <ToggleSwitch
            v-model="form.hasGlowEffect"
            :name="`${id}-hasGlowEffect`"
            :label="$t('has_glow_effect')"
            help="Glow effect"
        />

        <!-- Cards repeater -->
        <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between">
                <span class="text-sm font-medium text-gray-700">{{
                    $t('cards')
                }}</span>
                <button
                    type="button"
                    class="btn btn-primary space-x-2 bg-blue-500 text-white"
                    @click="addCard"
                >
                    <fa icon="fa-plus" />
                    <span>{{ $t('add_card') }}</span>
                </button>
            </div>

            <div
                v-for="(card, index) in form.cards"
                :key="index"
                class="flex flex-col gap-3 rounded-md border border-gray-200 p-4"
            >
                <div class="flex items-center justify-between">
                    <span class="text-sm font-semibold text-gray-600">
                        {{
                            card.title[activeLocale] ||
                            `${$t('card')} ${index + 1}`
                        }}
                    </span>
                    <div class="flex items-center gap-2">
                        <button
                            type="button"
                            class="btn btn-primary bg-gray-500 text-white"
                            @click="toggleCard(index)"
                        >
                            <fa
                                :icon="
                                    isCardExpanded(index)
                                        ? 'fa-chevron-down'
                                        : 'fa-chevron-down'
                                "
                                :class="{ 'rotate-180': isCardExpanded(index) }"
                            />
                        </button>
                        <button
                            type="button"
                            class="btn btn-primary bg-red-500 text-white"
                            @click="removeCard(index)"
                        >
                            <fa icon="fa-trash-can" />
                        </button>
                    </div>
                </div>

                <div
                    v-if="isCardExpanded(index)"
                    class="flex flex-col gap-3"
                >
                    <TextField
                        v-model="card.title[activeLocale]"
                        :name="`${id}-card-${index}-title`"
                        :label="$t('title')"
                        help="Card title"
                        :required="true"
                    />
                    <div class="my-4 w-full">
                        <label
                            class="mb-1 block text-sm font-medium"
                            :for="`${id}-card-${index}-description`"
                        >
                            {{ $t('description') }}
                        </label>
                        <textarea
                            :id="`${id}-card-${index}-description`"
                            v-model="card.description[activeLocale]"
                            :name="`${id}-card-${index}-description`"
                            class="form-input h-56 w-full"
                        />
                    </div>
                    <TextField
                        v-model="card.icon"
                        :name="`${id}-card-${index}-icon`"
                        :label="$t('icon')"
                        help="Icon class name"
                    />
                    <div class="flex gap-4">
                        <SelectField
                            v-model="card.iconBackground"
                            :name="`${id}-card-${index}-iconBackground`"
                            :label="$t('icon_background')"
                            help="Icon background color"
                            :options="colorOptions"
                        />
                        <SelectField
                            v-model="card.iconColor"
                            :name="`${id}-card-${index}-iconColor`"
                            :label="$t('icon_color')"
                            help="Icon color"
                            :options="colorOptions"
                        />
                    </div>
                    <div class="flex gap-4">
                        <SelectField
                            v-model="card.align"
                            :name="`${id}-card-${index}-align`"
                            :label="$t('alignment')"
                            help="Card content alignment"
                            :options="alignOptions"
                        />
                        <SelectField
                            v-model="card.orientation"
                            :name="`${id}-card-${index}-orientation`"
                            :label="$t('orientation')"
                            :help="$t('card_layout_orientation')"
                            :options="orientationOptions"
                        />
                    </div>
                </div>
            </div>
        </div>
    </BaseFormWrapper>
</template>

<script setup lang="ts">
import { CardsBlock } from '@builder/types'
import { defaultCard, defaultCardsBlock } from '@builder/defaults'
import { colorOptions } from '@src/partials/pageBuilder/options'
import SelectField from '@src/components/SelectField.vue'
import TextField from '@src/components/TextField.vue'
import ToggleSwitch from '@src/components/ToggleSwitch.vue'
import { useDebounceFn } from '@vueuse/core'
import { ref, watch } from 'vue'
import BaseFormWrapper from './BaseFormWrapper.vue'

const props = defineProps<{
    id: string
    activeLocale: string
    initialData?: CardsBlock | undefined
}>()
const emit = defineEmits(['update:form'])

const form = ref<CardsBlock>(
    props.initialData
        ? { ...props.initialData, cards: [...props.initialData.cards] }
        : defaultCardsBlock(props.id)
)

const expandedCards = ref<Set<number>>(new Set<number>())

const alignOptions = [
    { label: 'Left', value: 'left' },
    { label: 'Center', value: 'center' },
    { label: 'Right', value: 'right' },
]

const orientationOptions = [
    { label: 'Vertical', value: 'vertical' },
    { label: 'Horizontal', value: 'horizontal' },
]

const addCard = () => {
    form.value.cards.push(defaultCard())
    expandedCards.value.add(form.value.cards.length - 1)
}

const removeCard = (index: number) => {
    form.value.cards.splice(index, 1)
    const updatedExpandedCards = new Set<number>()
    expandedCards.value.forEach((expandedIndex) => {
        if (expandedIndex < index) {
            updatedExpandedCards.add(expandedIndex)
            return
        }

        if (expandedIndex > index) {
            updatedExpandedCards.add(expandedIndex - 1)
        }
    })
    expandedCards.value = updatedExpandedCards
}

const isCardExpanded = (index: number): boolean => {
    return expandedCards.value.has(index)
}

const toggleCard = (index: number) => {
    if (expandedCards.value.has(index)) {
        expandedCards.value.delete(index)
        return
    }

    expandedCards.value.add(index)
}

const debouncedEmit = useDebounceFn((newVal: CardsBlock) => {
    emit('update:form', newVal)
}, 1000)

watch(
    form,
    (newVal: CardsBlock) => {
        debouncedEmit(newVal)
    },
    { deep: true }
)
</script>
